import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import FmcForm from 'components/FmcForm';
import InstallmentsPlaceOrderView from './InstallmentsPlaceOrderView';
import { selectCreditApplication } from 'state/creditApplication';
import {
  InstallmentsOffer,
  selectFinanceResult,
  selectFinanceOffersByType,
  getInstallmentOfferData,
} from 'state/financeOffers';
import useUnifiedDisclosures, {
  UnifiedDisclosuresResult,
} from 'hooks/requests/unifiedDisclosures';
import { RouteComponentProps } from 'react-router-dom';
import { selectTenant } from 'state/tenant';
import { useSnackbar } from 'notistack';
import useTranslate from 'hooks/useTranslate/useTranslate';
import useCheckout from '../useCheckout';
import usePrepareCheckout from '../usePrepareCheckout';
import useFeature from 'hooks/useFeature';

export interface FormValues {
  consent: boolean;
}

type PropTypes = RouteComponentProps<{
  id: string;
}>;

const usePrerequisite = (props: PropTypes) => {
  const { bread } = useSelector(selectTenant);
  if (!bread) {
    // Should never happens
    throw new Error('Invalid tenant config');
  }
  const { id: financingOfferId } = props.match.params;

  const { intendedPurchaseAmount } = useSelector(selectCreditApplication);

  if (!intendedPurchaseAmount) {
    throw new Error('No intended purchase amount in state');
  }

  const financeResult = useSelector(selectFinanceResult);
  if (financeResult === null) {
    // Should not be possible to reach this page without the application.
    throw new Error('No application in state');
  }

  const { installmentOffers } = useSelector(selectFinanceOffersByType);
  const selectedOffer = useMemo(() => {
    return installmentOffers.find(
      financeOffer => financeOffer.id === financingOfferId,
    );
  }, [financingOfferId, installmentOffers]);

  if (!selectedOffer) {
    throw new Error(`Invalid finance offer id: ${financingOfferId}`);
  }

  return {
    bread,
    intendedPurchaseAmount,
    financeResult,
    selectedOffer,
  };
};

const InstallmentPlaceOrder: React.FC<PropTypes> = props => {
  const application = useSelector(selectCreditApplication);
  const [unifiedDisclosureRequest] = useUnifiedDisclosures();
  const { enqueueSnackbar } = useSnackbar();
  const t = useTranslate();
  const { isEnabled } = useFeature();
  const {
    intendedPurchaseAmount,
    financeResult,
    selectedOffer,
  } = usePrerequisite(props);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        consent: Yup.boolean()
          .test('isChecked', 'must consent', consent => consent)
          .default(false),
      }),
    [],
  );

  const initialValues: FormValues = {
    consent: false,
  } as FormValues;

  const checkoutFunction = isEnabled('financingWallet')
    ? usePrepareCheckout
    : useCheckout;

  const checkout = checkoutFunction(financeResult, selectedOffer);

  const [hasDisclosureError, setHasDisclosureError] = useState<boolean>(false);

  const handleDisclosureError = useCallback(() => {
    enqueueSnackbar(t('unified.placeOrder.error.genericSnackbarError'), {
      variant: 'error',
      persist: true,
    });
    setHasDisclosureError(true);
  }, [enqueueSnackbar, t]);

  const [disclosureLoading, setDisclosureLoading] = useState<{
    [type: string]: boolean;
  }>({ 'loan-agreement': true });

  const setDisclosureLoadingState = useCallback(
    (type: string, loading: boolean) => {
      setDisclosureLoading({ [type]: loading });
    },
    [],
  );

  const getDisclosureLoadingState = useCallback(
    (type?: string) => {
      if (type) {
        return disclosureLoading[type];
      }
      for (const propName of Object.getOwnPropertyNames(disclosureLoading)) {
        if (disclosureLoading[propName]) {
          return true;
        }
      }
      return false;
    },
    [disclosureLoading],
  );

  const [cachedDisclosureResult, setCachedDisclosureResult] = useState<
    UnifiedDisclosuresResult | undefined
  >(undefined);

  const getDisclosureResult = useCallback(async () => {
    try {
      const paymentAgreementId = selectedOffer.paymentAgreementId;

      setDisclosureLoadingState('loan-agreement', true);
      if (!cachedDisclosureResult) {
        const cachedDisclosureResult = await unifiedDisclosureRequest({
          paymentAgreementId,
          type: 'loan-agreement',
        });
        setCachedDisclosureResult(cachedDisclosureResult);
      }
      return cachedDisclosureResult;
    } catch (e) {
      handleDisclosureError();
    } finally {
      setDisclosureLoadingState('loan-agreement', false);
    }
  }, [
    cachedDisclosureResult,
    handleDisclosureError,
    selectedOffer.paymentAgreementId,
    setDisclosureLoadingState,
    unifiedDisclosureRequest,
  ]);

  useEffect(() => {
    getDisclosureResult();
  }, [getDisclosureResult]);

  const installmentOfferData = getInstallmentOfferData(
    parseFloat(intendedPurchaseAmount),
    selectedOffer as InstallmentsOffer,
  );
  const handleSubmit = useCallback(async () => {
    if (!cachedDisclosureResult) {
      throw new Error(
        'loan-agreement must have been successfully fetched before submitting',
      );
    }
    await checkout({
      disclosureChecksum: cachedDisclosureResult.disclosureChecksum,
    });
  }, [cachedDisclosureResult, checkout]);

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {formProps => (
        <InstallmentsPlaceOrderView
          application={application}
          installmentOfferData={installmentOfferData}
          paymentAgreementId={selectedOffer.paymentAgreementId}
          cachedLoanAgreementDisclosureContent={
            cachedDisclosureResult
              ? cachedDisclosureResult.disclosureContent
              : ''
          }
          hasDisclosureError={hasDisclosureError}
          handleDisclosureError={handleDisclosureError}
          getDisclosureLoadingState={getDisclosureLoadingState}
          setDisclosureLoadingState={setDisclosureLoadingState}
          {...formProps}
        />
      )}
    </FmcForm>
  );
};

export default InstallmentPlaceOrder;
